/**
 * The channel between the update prompt and the app tree (T46, T71).
 *
 * `update-prompt.tsx` is raised from OUTSIDE React — `register.ts` imports it
 * lazily the first time the service worker reports a waiting version — while
 * the only `ToastProvider` lives in the app tree (`app/shell.tsx`). This is
 * the seam: the prompt raises a signal here, and `UpdatePromptHost`
 * (`update-host.tsx`) reads it with `useSyncExternalStore` and notifies.
 *
 * It imports nothing, so the host can subscribe without pulling the prompt's
 * chunk — and with it the workout store — into the first load.
 *
 * `key` counts the raisings. `apply` is the same function object every time
 * (the prompt's module-level `runApply`), so the key is what tells the host
 * that a prompt held back for a workout has been raised again.
 */

export interface UpdateSignal {
  /** What "Atualizar" runs, or `null` while no prompt is up. */
  readonly apply: (() => void) | null;
  /** Bumped by every {@link raise}; never reset. */
  readonly key: number;
}

let signal: UpdateSignal = { apply: null, key: 0 };
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

/** Raise the prompt. A second raise bumps the key, so the host re-notifies. */
export function raise(apply: () => void): void {
  signal = { apply, key: signal.key + 1 };
  emit();
}

/** Take the prompt down. A no-op when none is up, so the snapshot is stable. */
export function lower(): void {
  if (signal.apply === null) {
    return;
  }
  signal = { apply: null, key: signal.key };
  emit();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** A new object only when something changed: `useSyncExternalStore` needs that. */
export function get(): UpdateSignal {
  return signal;
}
